import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import ResultCard from "@/components/ResultCard";

const PRESETS = ["skibidi", "no cap", "rizz", "💀", "glazing", "delulu"];

export default function GuestDecodeBox({ status, onStatus }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const nav = useNavigate();

  const remaining = status ? status.remaining : 3;

  const submit = async (value) => {
    const input = (value ?? text).trim();
    setError("");
    if (!input) { setError("Type something to decode"); return; }
    if (input.length > 500) { setError("That's a bit long — keep it under 500 characters"); return; }
    if (remaining <= 0) { setLocked(true); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/guest/decode", { tool: "text", text: input });
      setResult(data);
      if (data._guest && onStatus) onStatus(data._guest);
      if (data._guest && data._guest.remaining <= 0) setLocked(true);
    } catch (e) {
      if (e.response?.status === 403) { setLocked(true); }
      else setError(apiErr(e.response?.data?.detail) || "We couldn't decode that. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="dk-raised rounded-2xl p-6 border-2" style={{ borderColor: "var(--card-alt)" }} data-testid="guest-decode-box">
        <label className="block text-sm font-semibold mb-2">Type a word, phrase or emoji your child uses</label>
        <textarea
          className="dk-input resize-none"
          rows={2}
          value={text}
          maxLength={520}
          disabled={locked}
          placeholder="e.g. “she's so delulu 💀”"
          onChange={(e) => setText(e.target.value)}
          data-testid="guest-decode-input"
        />
        {error && <p className="text-sm mt-2" style={{ color: "var(--error)" }} data-testid="guest-decode-error">{error}</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          {PRESETS.map((p) => (
            <button key={p} disabled={locked} onClick={() => { setText(p); submit(p); }}
              className="badge-soft hover:opacity-80 transition-opacity" data-testid="guest-decode-preset-chip">{p}</button>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4 gap-3">
          <span className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="guest-tries-left">
            {remaining} of {status?.limit ?? 3} free tries left
          </span>
          <button className="btn btn-primary" onClick={() => submit()} disabled={loading || locked} data-testid="guest-decode-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loading ? "Decoding…" : "Decode it"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="dk-card p-6 space-y-3 anim-in">
          <div className="h-5 w-40 rounded" style={{ background: "var(--card-alt)" }} />
          <div className="h-4 w-full rounded" style={{ background: "var(--card-alt)" }} />
          <div className="h-4 w-2/3 rounded" style={{ background: "var(--card-alt)" }} />
        </div>
      )}

      {result && !loading && <ResultCard result={result} />}

      {(locked || (result && !loading)) && (
        <div className="dk-card p-6 flex flex-col sm:flex-row items-center gap-4 justify-between anim-in" data-testid="guest-upsell">
          <div>
            <p className="font-serif text-[18px] font-semibold">{locked ? "You've used your 3 free tries" : "Want to save this and unlock every tool?"}</p>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Start your free 3-day trial — no card needed.</p>
          </div>
          <button className="btn btn-primary whitespace-nowrap" onClick={() => nav("/auth/signup")} data-testid="guest-start-trial-btn">Start free trial</button>
        </div>
      )}
    </div>
  );
}
